import {
  computed,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from "vue";
import { axios_instance } from "@/api/api";
import {
  generateCampaignReport,
  getExportTask,
  listCampaignReports,
  previewCampaignReport,
  type ExportTask,
  type ReportFilters,
  type ReportFormat,
  type ReportIntegrity,
  type ReportPreview,
} from "@/api/report";
import { downloadFileFromUrl } from "@/composables/useDownload";

export const REPORT_PAGE_SIZE = 20;
const pending = (t: ExportTask) =>
  t.status === "pending" || t.status === "processing";
export function integrityStatus(integrity?: ReportIntegrity | null) {
  if (!integrity) return { label: "Chưa có", cls: "badge-neutral" };
  if (integrity.blocked) return { label: "Bị chặn", cls: "badge-error" };
  if (integrity.score >= 95) return { label: "Hợp lệ", cls: "badge-success" };
  return { label: "Cảnh báo", cls: "badge-warning" };
}
export function reportStatusBadge(status: string) {
  return (
    (
      {
        pending: { label: "Đang chờ", cls: "badge-neutral" },
        processing: { label: "Đang xử lý", cls: "badge-info" },
        done: { label: "Hoàn thành", cls: "badge-success" },
        failed: { label: "Thất bại", cls: "badge-error" },
      } as Record<string, { label: string; cls: string }>
    )[status] ?? { label: status, cls: "badge-neutral" }
  );
}
const emptyDraft = () => ({
  from: "",
  to: "",
  data_type: "",
  agent_id: "",
  search: "",
});

export function useCampaignReport(campaignId: MaybeRefOrGetter<string>) {
  const draft = ref(emptyDraft());
  const appliedFilters = ref<ReportFilters>({
    campaign_id: toValue(campaignId),
  });
  const preview = ref<ReportPreview | null>(null);
  const previewError = ref<string | null>(null);
  const isLoadingPreview = ref(false);
  const evidencePage = ref(1);
  const reports = ref<ExportTask[]>([]);
  const totalFilter = ref(0);
  const page = ref(1);
  const isLoadingList = ref(false);
  const listError = ref<string | null>(null);
  const error = ref<string | null>(null);
  const isGenerating = ref(false);
  const downloading = ref<string | null>(null);
  const pollError = ref<string | null>(null);
  const activeTask = ref<ExportTask | null>(null);
  let generation = 0,
    previewRequest = 0,
    listRequest = 0;
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let polling = false;
  // Track pending tasks across history pages, not only the visible page.
  const tracked = new Map<string, ExportTask>();
  const validationError = computed(() => {
    for (const value of [draft.value.from, draft.value.to]) {
      if (
        value &&
        (!/^\d{4}-\d{2}-\d{2}$/.test(value) ||
          !Number.isFinite(Date.parse(value + "T00:00:00Z")) ||
          new Date(value + "T00:00:00Z").toISOString().slice(0, 10) !== value)
      )
        return "Ngày không hợp lệ (UTC).";
    }
    return draft.value.from &&
      draft.value.to &&
      draft.value.from > draft.value.to
      ? "Ngày bắt đầu phải trước hoặc bằng ngày kết thúc."
      : null;
  });
  const filters = computed<ReportFilters>(() => ({
    campaign_id: toValue(campaignId),
    ...(draft.value.from
      ? { from: Date.parse(draft.value.from + "T00:00:00Z") }
      : {}),
    ...(draft.value.to
      ? { to: Date.parse(draft.value.to + "T00:00:00Z") + 86400000 }
      : {}),
    ...(draft.value.data_type ? { data_type: draft.value.data_type } : {}),
    ...(draft.value.agent_id ? { agent_id: draft.value.agent_id } : {}),
    ...(draft.value.search.trim() ? { search: draft.value.search.trim() } : {}),
  }));
  const hasPendingFilters = computed(
    () =>
      (Object.keys(filters.value) as (keyof ReportFilters)[]).some(
        (key) => filters.value[key] !== appliedFilters.value[key],
      ) ||
      (Object.keys(appliedFilters.value) as (keyof ReportFilters)[]).some(
        (key) => filters.value[key] !== appliedFilters.value[key],
      ),
  );
  const canExport = computed(
    () =>
      !!preview.value &&
      !validationError.value &&
      !hasPendingFilters.value &&
      !isLoadingPreview.value &&
      !previewError.value &&
      !isGenerating.value,
  );
  const totalPages = computed(() =>
    Math.max(1, Math.ceil(totalFilter.value / REPORT_PAGE_SIZE)),
  );
  const evidencePages = computed(() =>
    Math.max(
      1,
      Math.ceil((preview.value?.total_filter ?? 0) / REPORT_PAGE_SIZE),
    ),
  );

  async function loadPreview(targetPage = 1) {
    const request = ++previewRequest;
    const epoch = generation;
    isLoadingPreview.value = true;
    previewError.value = null;
    // Never show old aggregates beneath a new filter scope.
    preview.value = null;
    try {
      const data = await previewCampaignReport(
        { ...appliedFilters.value },
        targetPage,
        REPORT_PAGE_SIZE,
      );
      if (disposed || epoch !== generation || request !== previewRequest)
        return;
      preview.value = data;
      evidencePage.value = targetPage;
    } catch {
      if (!disposed && epoch === generation && request === previewRequest)
        previewError.value = "Không tải được dữ liệu báo cáo. Thử lại.";
    } finally {
      if (!disposed && epoch === generation && request === previewRequest)
        isLoadingPreview.value = false;
    }
  }
  async function applyFilters() {
    if (validationError.value) return;
    appliedFilters.value = { ...filters.value };
    await loadPreview(1);
  }
  function stopPolling() {
    if (timer) clearTimeout(timer);
    timer = undefined;
  }
  function schedulePoll() {
    stopPolling();
    if (!disposed && tracked.size) timer = setTimeout(() => void poll(), 3000);
  }
  async function poll() {
    if (disposed || polling || !tracked.size) return;
    stopPolling();
    polling = true;
    pollError.value = null;
    const epoch = generation;
    try {
      for (const id of [...tracked.keys()]) {
        if (disposed || epoch !== generation) break;
        try {
          const task = await getExportTask(id);
          if (disposed || epoch !== generation) break;
          const index = reports.value.findIndex((t) => t.id === id);
          if (index >= 0) reports.value[index] = task;
          if (activeTask.value?.id === id) activeTask.value = task;
          if (pending(task)) tracked.set(id, task);
          else tracked.delete(id);
        } catch {
          if (!disposed && epoch === generation)
            pollError.value = "Mất kết nối cập nhật tiến độ; sẽ tự thử lại.";
        }
      }
    } finally {
      polling = false;
      schedulePoll();
    }
  }
  async function loadReports(targetPage = page.value) {
    const epoch = generation,
      request = ++listRequest;
    isLoadingList.value = true;
    listError.value = null;
    try {
      const res = await listCampaignReports(
        toValue(campaignId),
        targetPage,
        REPORT_PAGE_SIZE,
      );
      if (disposed || epoch !== generation || request !== listRequest) return;
      reports.value = res.data ?? [];
      totalFilter.value = res.total_filter;
      page.value = targetPage;
      for (const t of reports.value) {
        if (pending(t)) tracked.set(t.id, t);
        else tracked.delete(t.id);
      }
      if (!polling) schedulePoll();
    } catch {
      if (!disposed && epoch === generation && request === listRequest)
        listError.value = "Không tải được danh sách báo cáo.";
    } finally {
      if (!disposed && epoch === generation && request === listRequest)
        isLoadingList.value = false;
    }
  }
  async function setPage(targetPage: number) {
    if (targetPage >= 1 && targetPage <= totalPages.value)
      await loadReports(targetPage);
  }
  async function startGeneration(format: ReportFormat = "pdf") {
    if (isGenerating.value || !canExport.value) return;
    const epoch = generation;
    const scope = { ...appliedFilters.value, campaign_id: toValue(campaignId) };
    error.value = null;
    isGenerating.value = true;
    try {
      const res = await generateCampaignReport(scope, format);
      if (disposed || epoch !== generation) return;
      const task: ExportTask = {
        id: res.data.id,
        type: "report",
        format,
        campaign_id: scope.campaign_id,
        report_filters: scope,
        status: "pending",
        exported_records: 0,
        progress_percent: 0,
        attempts: 0,
        created: Date.now(),
        modified: Date.now(),
      };
      activeTask.value = task;
      reports.value = [task, ...reports.value];
      totalFilter.value++;
      tracked.set(task.id, task);
      schedulePoll();
    } catch {
      if (!disposed && epoch === generation)
        error.value =
          "Không thể khởi tạo báo cáo. Kiểm tra quyền quản lý và thử lại.";
    } finally {
      if (!disposed && epoch === generation) isGenerating.value = false;
    }
  }
  async function startDownload(taskId: string) {
    if (downloading.value) return;
    const epoch = generation;
    downloading.value = taskId;
    error.value = null;
    try {
      // The anchor helper cannot observe HTTP failures. Resolve the authenticated blob first.
      const response = await axios_instance.get(
        `/api/v1/view/browser-history/export/${encodeURIComponent(taskId)}/download`,
        { responseType: "blob" },
      );
      if (disposed || epoch !== generation) return;
      const url = URL.createObjectURL(response.data);
      try {
        downloadFileFromUrl(
          url,
          `report-${toValue(campaignId)}.${reports.value.find((t) => t.id === taskId)?.format ?? "pdf"}`,
        );
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch {
      if (!disposed && epoch === generation)
        error.value = "Không tải được tệp báo cáo. Vui lòng thử lại.";
    } finally {
      if (!disposed && epoch === generation) downloading.value = null;
    }
  }
  watch(
    () => toValue(campaignId),
    () => {
      generation++;
      stopPolling();
      tracked.clear();
      previewRequest++;
      listRequest++;
      reports.value = [];
      totalFilter.value = 0;
      page.value = 1;
      activeTask.value = null;
      error.value = null;
      pollError.value = null;
      downloading.value = null;
      isGenerating.value = false;
      draft.value = emptyDraft();
      appliedFilters.value = { campaign_id: toValue(campaignId) };
      void loadPreview();
      void loadReports(1);
    },
    { immediate: true },
  );
  onScopeDispose(() => {
    disposed = true;
    generation++;
    stopPolling();
    tracked.clear();
  });
  return {
    draft,
    appliedFilters,
    preview,
    previewError,
    isLoadingPreview,
    evidencePage,
    evidencePages,
    validationError,
    hasPendingFilters,
    canExport,
    applyFilters,
    loadPreview,
    reports,
    totalFilter,
    page,
    totalPages,
    isLoadingList,
    listError,
    loadReports,
    setPage,
    activeTask,
    error,
    isGenerating,
    downloading,
    pollError,
    integrityStatus,
    reportStatusBadge,
    startGeneration,
    startDownload,
    poll,
    stopPolling,
  };
}
